import React, { useState, useEffect } from 'react'
import st from '../MassEditDealsForm/MassEditDealsForm.module.scss' // переиспользуем стили
import Button from 'components/CRM/UI/components/Button/Button'
import Loader from 'components/UI/Loader/Loader'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import FormDateSelector from 'components/UI/AsideModal/Form/FormDateSelector'
import FormField from 'components/UI/AsideModal/Form/FormField'
import FormLabel from 'components/UI/AsideModal/Form/FormLabel'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import DatePicker from 'react-datepicker'
import { timeConverter } from 'components/ContentBox/Online-schedule/components/functions'
import { addZero } from 'helpers/math'
import { dateFormat } from 'helpers/date'
import { observer } from 'mobx-react'

const MassAddTasksForm = ({
  dealsForTasks,
  onClose,
  onSave,
  translate,
  funnelId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [selectedDeals, setSelectedDeals] = useState([])

  // Справочники
  const [executors, setExecutors] = useState([])
  const [taskCategories, setTaskCategories] = useState([])
  const [taskStatuses, setTaskStatuses] = useState([])

  // Значения полей
  const [values, setValues] = useState({
    assignee: null,
    task_category: null,
    status: null,
    date: '',
    start_at: '06:00:00',
    finish_at: '23:00:00',
    comment: '',
  })

  // Загрузка справочников
  useEffect(() => {
    let isMounted = true

    const fetchAll = async () => {
      try {
        const [executorsRes, categoriesRes, statusesRes] = await Promise.all([
          superAxiosRequest({
            link: 'integration/employees',
            method: 'get',
            crm: true,
            params: { limit: 999, short_form: true },
          }),
          superAxiosRequest({
            link: 'tasktracker/task_categories',
            method: 'get',
            crm: true,
          }),
          superAxiosRequest({
            link: 'tasktracker/task_statuses',
            method: 'get',
            crm: true,
          }),
        ])

        if (!isMounted) return

        const empList = (
          executorsRes.data?.result ||
          executorsRes.data ||
          []
        ).map((e) => ({ id: e.id, name: e.full_name || e.name }))
        setExecutors(empList)

        const cats = categoriesRes.data?.result || categoriesRes.data || []
        setTaskCategories(cats)

        const stats = statusesRes.data?.result || statusesRes.data || []
        setTaskStatuses(stats)

        // Дефолтные значения
        const defaultCategory =
          cats.find((c) => c.name === 'Contact') || cats[0]
        const defaultStatus = stats[0]

        setValues((prev) => ({
          ...prev,
          task_category: defaultCategory?.id || null,
          status: defaultStatus?.id || null,
        }))
      } catch (err) {
        console.error('Ошибка загрузки справочников:', err)
      }
    }

    fetchAll()

    return () => {
      isMounted = false
    }
  }, [])

  // Инициализация списка сделок
  useEffect(() => {
    if (dealsForTasks.length) {
      setSelectedDeals(dealsForTasks.map((deal) => ({ ...deal })))
    }
    setLoadingData(false)
  }, [dealsForTasks])

  const removeDeal = (dealId) => {
    if (selectedDeals.length <= 1) {
      swal('Внимание!', 'Должна быть выбрана хотя бы 1 сделка', 'warning')
      return
    }
    setSelectedDeals((prev) => prev.filter((deal) => deal.id !== dealId))
  }

  const handleChange = (field, value) => {
    let finalValue = value
    if (value && typeof value === 'object' && value.id !== undefined) {
      finalValue = value.id
    }
    setValues((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleDateChange = (val) => {
    const formattedDate = val ? dateFormat(val, 'YYYY-mm-dd') : ''
    setValues((prev) => ({ ...prev, date: formattedDate }))
  }

  const buildDateTimeISO = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null
    // timeStr имеет формат HH:mm:ss
    const [h, m, s] = timeStr.split(':')
    const localDate = new Date(
      `${dateStr}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:${(
        s || '00'
      ).padStart(2, '0')}`,
    )
    return localDate.toISOString()
  }

  const handleSave = async () => {
    if (isLoading) return

    if (selectedDeals.length < 1) {
      return swal('Ошибка!', 'Должна быть выбрана хотя бы 1 сделка', 'warning')
    }

    if (!values.assignee) {
      return swal('Ошибка!', 'Выберите исполнителя', 'warning')
    }
    if (!values.task_category) {
      return swal('Ошибка!', 'Выберите тип задачи', 'warning')
    }
    if (!values.status) {
      return swal('Ошибка!', 'Выберите статус задачи', 'warning')
    }
    if (!values.date) {
      return swal('Ошибка!', 'Выберите дату', 'warning')
    }
    if (!values.start_at || !values.finish_at) {
      return swal('Ошибка!', 'Укажите время начала и окончания', 'warning')
    }

    const startISO = buildDateTimeISO(values.date, values.start_at)
    const finishISO = buildDateTimeISO(values.date, values.finish_at)

    if (new Date(startISO) >= new Date(finishISO)) {
      return swal(
        'Ошибка!',
        'Время окончания должно быть больше времени начала',
        'warning',
      )
    }

    setIsLoading(true)

    try {
      const payload = selectedDeals.map((deal) => ({
        deal: deal.id,
        assignee: values.assignee,
        task_category: values.task_category,
        status: values.status,
        creator: deal.manager?.id ?? values.assignee, // подстраховка, если нет manager
        start_task: startISO,
        finish_task: finishISO,
        ...(values.comment ? { comment: values.comment } : {}),
      }))

      await onSave(payload)
    } catch (error) {
      console.error('Ошибка при создании задач:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getExecutorName = (id) =>
    executors.find((e) => e.id === id)?.name || 'Не выбрано'
  const getCategoryName = (id) =>
    taskCategories.find((c) => c.id === id)?.name || 'Не выбрано'
  const getStatusName = (id) =>
    taskStatuses.find((s) => s.id === id)?.name || 'Не выбрано'

  return (
    <div className={st.mergeModalOverlay}>
      <div className={st.mergeModal}>
        <div className={st.mergeModalHeader}>
          <h2>
            Массовое добавление задач — Будет создано задач:{' '}
            {selectedDeals.length}
          </h2>
          <button className={st.closeButton} onClick={onClose}>
            x
          </button>
        </div>

        <div className={st.mergeModalContent}>
          {loadingData ? (
            <div className={st.loadingContainer}>
              <Loader />
              <p>Загрузка данных...</p>
            </div>
          ) : (
            <>
              <div className={st.mergeInstructions}>
                <p>
                  Заполните поля задачи. Она будет создана для каждой выбранной
                  сделки.
                </p>
                <p className={st.note}>
                  Всего будет создано задач: <b>{selectedDeals.length}</b>
                </p>
              </div>

              {/* Список выбранных сделок */}
              <div className={st.selectedDealsSection}>
                <h3>Выбранные сделки:</h3>
                <div className={st.dealsList}>
                  {selectedDeals.map((deal, index) => (
                    <React.Fragment key={deal.id}>
                      <span className={st.dealItem}>
                        <span className={st.dealName}>
                          {deal.deal_name || `Сделка #${deal.id}`}
                          {deal.client?.name && ` (${deal.client.name})`}
                        </span>
                        <button
                          className={st.removeDealBtn}
                          onClick={() => removeDeal(deal.id)}
                          title='Удалить из списка'
                        >
                          x
                        </button>
                      </span>
                      {index < selectedDeals.length - 1 && (
                        <span className={st.dealSeparator}>,</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Форма задачи */}
              <div className={st.editForm}>
                <div className={st.fieldsGrid}>
                  {/* Исполнитель */}
                  <FormField>
                    <FormLabel isRequired>Исполнитель:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите исполнителя'
                      options={executors}
                      sortKey='name'
                      valueID={values.assignee}
                      onChange={(o) => handleChange('assignee', o)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Тип задачи */}
                  <FormField>
                    <FormLabel isRequired>Тип задачи:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите тип задачи'
                      options={taskCategories}
                      sortKey='name'
                      valueID={values.task_category}
                      onChange={(o) => handleChange('task_category', o)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Статус задачи */}
                  <FormField>
                    <FormLabel isRequired>Статус задачи:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите статус'
                      options={taskStatuses}
                      sortKey='name'
                      valueID={values.status}
                      onChange={(o) => handleChange('status', o)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Дата */}
                  <FormField>
                    <FormLabel isRequired>Дата:</FormLabel>
                    <FormDateSelector
                      placeholder='Выберите дату'
                      onChange={handleDateChange}
                      value={values.date || ''}
                      gap={0}
                    />
                  </FormField>

                  {/* Время начала / окончания */}
                  <FormField>
                    <FormLabel isRequired>Время начала/окончания:</FormLabel>
                    <FormInput>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <DatePicker
                          selected={
                            values.start_at
                              ? new Date('2020-01-01T' + values.start_at)
                              : new Date('2020-01-01T06:00:00')
                          }
                          onChange={(date) => {
                            const t =
                              timeConverter(date, 'time') +
                              `:${addZero(date.getSeconds())}`
                            setValues((prev) => ({ ...prev, start_at: t }))
                          }}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption='Начало'
                          timeFormat='HH:mm'
                          dateFormat='HH:mm'
                        />
                        <div>-</div>
                        <DatePicker
                          selected={
                            values.finish_at
                              ? new Date('2020-01-01T' + values.finish_at)
                              : new Date('2020-01-01T23:00:00')
                          }
                          onChange={(date) => {
                            const t =
                              timeConverter(date, 'time') +
                              `:${addZero(date.getSeconds())}`
                            setValues((prev) => ({ ...prev, finish_at: t }))
                          }}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption='Окончание'
                          timeFormat='HH:mm'
                          dateFormat='HH:mm'
                        />
                      </div>
                    </FormInput>
                  </FormField>

                  {/* Комментарий */}
                  {/*<FormField>*/}
                  {/*  <FormLabel>Комментарий:</FormLabel>*/}
                  {/*  <FormInput>*/}
                  {/*    <textarea*/}
                  {/*      value={values.comment}*/}
                  {/*      onChange={(e) =>*/}
                  {/*        setValues((prev) => ({*/}
                  {/*          ...prev,*/}
                  {/*          comment: e.target.value,*/}
                  {/*        }))*/}
                  {/*      }*/}
                  {/*      placeholder='Введите комментарий'*/}
                  {/*    />*/}
                  {/*  </FormInput>*/}
                  {/*</FormField>*/}
                </div>

                {/* Превью */}
                <div className={st.selectedFieldsInfo}>
                  <h4>Будет создано задач: {selectedDeals.length}</h4>
                  <div className={st.selectedFieldsList}>
                    <div className={st.selectedFieldItem}>
                      <span className={st.fieldLabel}>Исполнитель:</span>
                      <span className={st.fieldValue}>
                        {getExecutorName(values.assignee)}
                      </span>
                    </div>
                    <div className={st.selectedFieldItem}>
                      <span className={st.fieldLabel}>Тип задачи:</span>
                      <span className={st.fieldValue}>
                        {getCategoryName(values.task_category)}
                      </span>
                    </div>
                    <div className={st.selectedFieldItem}>
                      <span className={st.fieldLabel}>Статус:</span>
                      <span className={st.fieldValue}>
                        {getStatusName(values.status)}
                      </span>
                    </div>
                    <div className={st.selectedFieldItem}>
                      <span className={st.fieldLabel}>Дата и время:</span>
                      <span className={st.fieldValue}>
                        {values.date || 'Не выбрано'} {values.start_at} —{' '}
                        {values.finish_at}
                      </span>
                    </div>
                  </div>
                  <p className={st.selectedFieldsNote}>
                    *Задача будет создана для каждой выбранной сделки
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={st.mergeActions}>
          <Button
            text='Отмена'
            onClick={onClose}
            className={st.cancelButton}
            isDisabled={isLoading}
          />
          <Button
            text={
              isLoading
                ? 'Создание...'
                : `Создать задачи (${selectedDeals.length})`
            }
            onClick={handleSave}
            className={st.mergeButton}
          />
        </div>
      </div>
    </div>
  )
}

export default observer(MassAddTasksForm)
